import { useRef, useState } from "react";
import { useAuth } from "../contexts/AuthContext";
import api, { resolveImageUrl } from "../lib/api";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import {
  Loader2,
  Camera,
  User,
  Wallet,
  CalendarDays,
  Languages,
  LifeBuoy,
  LogOut,
  ChevronRight,
  Car,
  GraduationCap,
  BellOff,
  Share2,
  QrCode,
} from "lucide-react";
import { Switch } from "./ui/switch";
import OnboardingModal from "./OnboardingModal";
import ShareQrDialog from "./ShareQrDialog";
import ShareAppDialog from "./ShareAppDialog";
import ReferralStatsCard from "./ReferralStatsCard";
import { SUPPORT_WHATSAPP_URL } from "../lib/support";
import { shareApp } from "../lib/share";

function MenuRow({ icon: Icon, title, subtitle, onClick, testId }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="w-full flex items-center gap-4 px-5 py-4 hover:bg-[#F8FAFC] transition text-left"
      data-testid={testId}
    >
      <span className="w-11 h-11 rounded-2xl bg-[#F1F5F9] flex items-center justify-center shrink-0">
        <Icon className="w-5 h-5 text-[#0A2540]" strokeWidth={2} />
      </span>
      <span className="flex-1 min-w-0">
        <span className="block text-[15px] font-bold text-[#0A2540] leading-tight" style={{ fontFamily: "Outfit" }}>
          {title}
        </span>
        <span className="block text-[12px] text-[#64748B] mt-0.5">{subtitle}</span>
      </span>
      <ChevronRight className="w-5 h-5 text-[#CBD5E1]" />
    </button>
  );
}

export default function PassengerProfile() {
  const { user, checkAuth, logout } = useAuth();
  const navigate = useNavigate();
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [preview, setPreview] = useState(null);
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [showQr, setShowQr] = useState(false);
  const [showShare, setShowShare] = useState(false);
  const [silentSaving, setSilentSaving] = useState(false);
  const silent = !!user?.silent_notifications;

  const toggleSilent = async (next) => {
    setSilentSaving(true);
    try {
      await api.put("/auth/profile", { silent_notifications: next });
      await checkAuth?.();
      toast.success(next ? "Mode silencieux activé" : "Mode silencieux désactivé");
    } catch (err) {
      toast.error("Impossible de mettre à jour la préférence");
    } finally {
      setSilentSaving(false);
    }
  };

  const handlePick = () => fileRef.current?.click();

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Image trop volumineuse (max 5 Mo)");
      return;
    }
    const localUrl = URL.createObjectURL(file);
    setPreview(localUrl);
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      await api.post("/upload/profile-photo", fd, { headers: { "Content-Type": "multipart/form-data" } });
      await checkAuth?.();
      toast.success("Photo de profil mise à jour");
    } catch (err) {
      toast.error("Échec de l'upload", { description: err.response?.data?.detail || "Erreur" });
      setPreview(null);
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const handleLogout = async () => {
    try {
      await logout?.();
    } catch (_e) {
      /* noop */
    }
    navigate("/", { replace: true });
  };

  const currentPhoto = preview || (user?.profile_photo ? resolveImageUrl(user.profile_photo) : null);
  const initial = (user?.name || "?").trim().charAt(0).toUpperCase();

  return (
    <div className="space-y-5" data-testid="passenger-profile-panel" style={{ fontFamily: "Outfit, Inter, sans-serif" }}>
      <h2 className="text-2xl sm:text-3xl font-extrabold text-[#0A2540]" style={{ fontFamily: "Outfit" }}>
        Profil
      </h2>

      {/* Identity card — dark rounded */}
      <div
        className="bg-[#0A2540] rounded-3xl p-4 flex items-center gap-4 text-white"
        data-testid="profile-identity-card"
      >
        <div className="relative shrink-0">
          {currentPhoto ? (
            <img
              src={currentPhoto}
              alt="Profil"
              className="w-16 h-16 rounded-full object-cover border-2 border-[#FACC15]"
              data-testid="passenger-profile-photo"
            />
          ) : (
            <div
              className="w-16 h-16 rounded-full bg-[#FACC15] flex items-center justify-center text-[#0A2540] text-2xl font-extrabold"
              data-testid="passenger-profile-photo-placeholder"
            >
              {initial}
            </div>
          )}
          <button
            type="button"
            onClick={handlePick}
            disabled={uploading}
            className="absolute -bottom-1 -right-1 w-7 h-7 rounded-full bg-white text-[#0A2540] flex items-center justify-center shadow-md disabled:opacity-50"
            data-testid="passenger-upload-photo-btn"
            title="Changer la photo"
          >
            {uploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Camera className="w-3.5 h-3.5" />}
          </button>
          <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={handleUpload} data-testid="passenger-photo-input" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-xl font-extrabold truncate" data-testid="profile-user-name">
            {user?.name || "—"}
          </p>
          {user?.phone && <p className="text-sm text-white/70 truncate">{user.phone}</p>}
          {user?.role === "driver" ? (
            <span className="inline-flex items-center gap-1.5 mt-2 bg-[#FACC15] text-[#0A2540] rounded-full px-3 py-1 text-xs font-bold">
              <Car className="w-3 h-3" /> Chauffeur
            </span>
          ) : (
            <span className="inline-flex items-center gap-1.5 mt-2 bg-[#FACC15] text-[#0A2540] rounded-full px-3 py-1 text-xs font-bold">
              <User className="w-3 h-3" /> Passager
            </span>
          )}
        </div>
      </div>

      {/* Menu list */}
      <div
        className="bg-white rounded-3xl border border-[#E2E8F0] overflow-hidden divide-y divide-[#F1F5F9]"
        data-testid="profile-menu-list"
      >
        <MenuRow
          icon={Wallet}
          title="Modes de paiement"
          subtitle="MTN Mobile Money · Orange Money"
          onClick={() => navigate("/passenger?tab=wallet")}
          testId="menu-payment-methods"
        />
        <MenuRow
          icon={CalendarDays}
          title="Mes réservations"
          subtitle="Historique et courses actives"
          onClick={() => navigate("/passenger?tab=bookings")}
          testId="menu-my-bookings"
        />
        <MenuRow
          icon={GraduationCap}
          title="Revoir le tutoriel"
          subtitle="Découvrez à nouveau nos services en 3 étapes"
          onClick={() => setShowOnboarding(true)}
          testId="menu-tutorial"
        />
        {/* Silent Mode toggle (son + vibration) */}
        <div className="w-full flex items-center gap-4 px-5 py-4" data-testid="menu-silent-mode">
          <span className="w-11 h-11 rounded-2xl bg-[#F1F5F9] flex items-center justify-center shrink-0">
            <BellOff className="w-5 h-5 text-[#0A2540]" strokeWidth={2} />
          </span>
          <span className="flex-1 min-w-0">
            <span className="block text-[15px] font-bold text-[#0A2540] leading-tight" style={{ fontFamily: "Outfit" }}>
              Mode silencieux
            </span>
            <span className="block text-[12px] text-[#64748B] mt-0.5">
              Coupe le son et la vibration des messages Flash
            </span>
          </span>
          <Switch
            checked={silent}
            disabled={silentSaving}
            onCheckedChange={toggleSilent}
            data-testid="silent-mode-toggle"
          />
        </div>
        <MenuRow
          icon={Languages}
          title="Langue"
          subtitle="Français"
          onClick={() => toast.info("Une seule langue disponible pour l'instant")}
          testId="menu-language"
        />
        <MenuRow
          icon={LifeBuoy}
          title="Aide & Support"
          subtitle="Contactez-nous à tout moment"
          onClick={() => window.open(SUPPORT_WHATSAPP_URL("Bonjour TrigoTour, j'ai besoin d'aide."), "_blank")}
          testId="menu-help"
        />
        <MenuRow
          icon={Share2}
          title="Partager l'application"
          subtitle="Invitez vos amis à essayer TrigoTour"
          onClick={() => setShowShare(true)}
          testId="menu-share-app"
        />
        <MenuRow
          icon={QrCode}
          title="Partager via QR code"
          subtitle="Idéal pour flyers, cartes de visite, vitrines"
          onClick={() => setShowQr(true)}
          testId="menu-share-qr"
        />
      </div>

      <ReferralStatsCard />

      {/* Logout */}
      <button
        type="button"
        onClick={handleLogout}
        className="w-full inline-flex items-center justify-center gap-2 h-14 rounded-full bg-[#FEE2E2] text-[#EF4444] font-bold text-base hover:bg-[#FECACA] transition"
        data-testid="profile-logout-btn"
      >
        <LogOut className="w-4 h-4" /> Se déconnecter
      </button>

      <p className="text-center text-xs text-[#94A3B8]" data-testid="profile-version">
        TrigoTour · v1.0
      </p>

      <OnboardingModal open={showOnboarding} onClose={() => setShowOnboarding(false)} />
      <ShareQrDialog open={showQr} onOpenChange={setShowQr} />
      <ShareAppDialog open={showShare} onOpenChange={setShowShare} />
    </div>
  );
}
